/**
 * Snapchat Ads — Row Transformers (Story 19.8, Task 4).
 *
 * Pure functions that convert raw Snap stats rows
 * (`Record<string, unknown>` with snake_case Snap field names) into typed
 * camelCase records aligned with `SNAPCHAT_ADS_METRICS[].key`.
 *
 * Conventions:
 *   - `parseMetric` — null/undefined/non-finite → 0 (matches Google Ads /
 *     MS Ads / Pinterest convention).
 *   - `parseMicroCurrency` — `parseMetric / 1_000_000`. **Sign-preserving** —
 *     Snap can return negative `spend` for refund/correction days.
 *   - `parseSnapPercent` — defensive normaliser. Snap docs say rates are
 *     decimals like `0.0234`, but `swipe_up_percent` has been observed in
 *     display-percent form. The `value > 1 ? /100 : value` guard handles
 *     both forms.
 *   - `parseDurationMillis` — pass-through `parseMetric`. Snap durations
 *     stay in milliseconds (widget formatters convert to display unit).
 *   - **`_app` / `_web` / total preservation** — for any conversion event
 *     where the raw row has both suffixed variants, the transformer emits
 *     all 3 keys (`X`, `XApp`, `XWeb`). Never collapsed.
 *   - Every transformer stamps `_reportType` on its output (Pre-mortem F4
 *     contract).
 *   - `safeRoas(num, den)` returns `0` on zero/negligible denominator (no
 *     `NaN` / `Infinity` leaking into widgets).
 *
 * @see _bmad/bmm/data/integration-metrics/snapchat-ads.md
 */

import type { SnapchatAdsReportType } from "./analytics";

// ============================================================================
// Primitive parsers
// ============================================================================

/**
 * Coerce a raw Snap cell into a finite number. Returns `0` for any
 * null/undefined/non-finite input. **Sign-preserving** — negative spend for
 * refunds MUST NOT be clamped to 0 (Pre-mortem F5).
 */
export function parseMetric(v: unknown): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Divide a Snap micro-currency integer by 1M. Sign-preserving.
 * `5_000_000` → `5.0`; `-5_000_000` → `-5.0`; `null` → `0`.
 */
export function parseMicroCurrency(v: unknown): number {
  return parseMetric(v) / 1_000_000;
}

/**
 * Defensive percent normaliser.
 *   - Fraction form (`0.0234`) → unchanged.
 *   - Display-percent form (`2.34`) → `0.0234`.
 *   - `null`/non-finite → `0`.
 */
export function parseSnapPercent(v: unknown): number {
  const n = parseMetric(v);
  return n > 1 ? n / 100 : n;
}

/** Snap durations are reported in milliseconds; preserve the unit. */
export function parseDurationMillis(v: unknown): number {
  return parseMetric(v);
}

/** Coerce a string identifier preserving null-ish → empty string. */
function parseString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

/**
 * Divide-by-zero guard for derived ratio metrics (ROAS, CPM, CPSU, etc.).
 * Returns `0` when the denominator is zero or negligible (`< 0.01`) to
 * avoid `Infinity` / `NaN` leaking into widget renders.
 */
export function safeRoas(numerator: number, denominator: number): number {
  if (!Number.isFinite(denominator)) return 0;
  if (Math.abs(denominator) < 0.01) return 0;
  if (!Number.isFinite(numerator)) return 0;
  return numerator / denominator;
}

// ============================================================================
// Field maps — single source of truth for Snap snake_case → camelCase key
// ============================================================================

interface FieldSpec {
  /** camelCase key emitted on the output record. */
  readonly out: string;
  /** Coercion function. Defaults to `parseMetric` (Integer/Decimal/Ratio). */
  readonly parse: (v: unknown) => number | string;
}

const M = (
  out: string,
  parse: FieldSpec["parse"] = parseMetric
): FieldSpec => ({ out, parse });

// ----------------------------------------------------------------------------
// Conversion event registry — one entry per event; the field map below
// expands each to 3 variants (total / _app / _web). The transformer treats
// all 3 as independent keys — never collapsing.
// ----------------------------------------------------------------------------

interface ConversionEvent {
  /** Snap snake_case base name (no `_app`/`_web` suffix). */
  readonly snake: string;
  /** Output camelCase base name. */
  readonly camel: string;
  /** Whether this event has a `_value` Currency variant in addition to count. */
  readonly hasValue?: boolean;
  /** True for the conversion_rate metric — uses parseSnapPercent. */
  readonly isRate?: boolean;
}

const CONVERSION_EVENTS: ReadonlyArray<ConversionEvent> = [
  { snake: "page_views", camel: "pageViews" },
  { snake: "view_content", camel: "viewContent" },
  { snake: "add_cart", camel: "addCart" },
  { snake: "start_checkout", camel: "startCheckout" },
  { snake: "add_billing", camel: "addBilling" },
  { snake: "purchases", camel: "purchases", hasValue: true },
  { snake: "sign_ups", camel: "signUps" },
  { snake: "login", camel: "logins" },
  { snake: "save", camel: "saves" },
  { snake: "searches", camel: "searches" },
  { snake: "share", camel: "shares" },
  { snake: "app_opens", camel: "appOpens" },
  { snake: "start_trial", camel: "startTrial" },
  { snake: "subscribe", camel: "subscribe" },
  { snake: "add_to_wishlist", camel: "addToWishlist" },
  { snake: "list_view", camel: "listView" },
  { snake: "reserve", camel: "reserve" },
  { snake: "level_completes", camel: "levelCompletes" },
  { snake: "complete_tutorial", camel: "completeTutorial" },
  { snake: "achievement_unlocked", camel: "achievementUnlocked" },
  { snake: "spend_credits", camel: "spendCredits" },
  { snake: "invite", camel: "invite" },
  { snake: "ad_click", camel: "adClick" },
  { snake: "ad_view", camel: "adView" },
  { snake: "rate", camel: "rate", isRate: true },
];

const CUSTOM_EVENT_SLOTS = [1, 2, 3, 4, 5] as const;

/**
 * Build the master Snap → spec map by composing static rows + the
 * algorithmically-expanded conversion + custom-event variants.
 */
function buildFieldMap(): Record<string, FieldSpec> {
  const map: Record<string, FieldSpec> = {
    // ------------------ Delivery (6) ------------------
    impressions: M("impressions"),
    uniques: M("uniques"),
    frequency: M("frequency"),
    screen_time_millis: M("screenTimeMillis", parseDurationMillis),
    avg_screen_time_millis: M("avgScreenTimeMillis", parseDurationMillis),
    spend: M("spend", parseMicroCurrency),

    // ------------------ Engagement (3) ------------------
    swipes: M("swipeUps"),
    swipe_up_percent: M("swipeUpRate", parseSnapPercent),
    view_time_millis: M("viewTimeMillis", parseDurationMillis),

    // ------------------ Video — Main (7) ------------------
    video_views: M("videoViews2s"),
    video_views_15s: M("videoViews15s"),
    video_views_time_based: M("videoViewsTimeBased"),
    view_completion: M("viewCompletion"),
    quartile_1: M("videoQuartile1"),
    quartile_2: M("videoQuartile2"),
    quartile_3: M("videoQuartile3"),

    // ------------------ Video — Attachment (9) ------------------
    attachment_impressions: M("attachmentImpressions"),
    attachment_uniques: M("attachmentUniques"),
    attachment_frequency: M("attachmentFrequency"),
    attachment_avg_view_time_millis: M(
      "attachmentAvgViewTimeMillis",
      parseDurationMillis
    ),
    attachment_total_view_time_millis: M(
      "attachmentTotalViewTimeMillis",
      parseDurationMillis
    ),
    attachment_view_completion: M("attachmentViewCompletion"),
    attachment_quartile_1: M("attachmentQuartile1"),
    attachment_quartile_2: M("attachmentQuartile2"),
    attachment_quartile_3: M("attachmentQuartile3"),

    // ------------------ App Installs (9) ------------------
    total_installs: M("totalInstalls"),
    total_installs_app: M("totalInstallsApp"),
    total_installs_web: M("totalInstallsWeb"),
    ios_installs: M("iosInstalls"),
    ios_installs_app: M("iosInstallsApp"),
    ios_installs_web: M("iosInstallsWeb"),
    android_installs: M("androidInstalls"),
    android_installs_app: M("androidInstallsApp"),
    android_installs_web: M("androidInstallsWeb"),

    // ------------------ Entity identifiers (string-typed) ------------------
    campaign_id: M("campaignId", parseString),
    campaign_name: M("campaignName", parseString),
    campaign_status: M("campaignStatus", parseString),
    campaign_objective: M("campaignObjective", parseString),
    adsquad_id: M("adsquadId", parseString),
    adsquad_name: M("adsquadName", parseString),
    adsquad_status: M("adsquadStatus", parseString),
    ad_id: M("adId", parseString),
    ad_name: M("adName", parseString),
    ad_status: M("adStatus", parseString),
    creative_id: M("creativeId", parseString),
    creative_name: M("creativeName", parseString),
    creative_type: M("creativeType", parseString),
    start_time: M("startTime", parseString),
    end_time: M("endTime", parseString),

    // ------------------ Dimension values (string-typed) ------------------
    age_bucket: M("ageBucket", parseString),
    age: M("ageBucket", parseString),
    gender: M("gender", parseString),
    country: M("country", parseString),
    region: M("region", parseString),
    dma: M("dma", parseString),
    os: M("os", parseString),
    make: M("make", parseString),
  };

  // Conversion events — emit total + _app + _web variants for each.
  for (const ev of CONVERSION_EVENTS) {
    const baseSnake = `conversion_${ev.snake}`;
    const baseCamel = `conversion${capitalize(ev.camel)}`;
    const parseFn = ev.isRate ? parseSnapPercent : parseMetric;

    map[baseSnake] = M(baseCamel, parseFn);
    map[`${baseSnake}_app`] = M(`${baseCamel}App`, parseFn);
    map[`${baseSnake}_web`] = M(`${baseCamel}Web`, parseFn);

    if (ev.hasValue) {
      map[`${baseSnake}_value`] = M(`${baseCamel}Value`, parseMicroCurrency);
      map[`${baseSnake}_value_app`] = M(
        `${baseCamel}ValueApp`,
        parseMicroCurrency
      );
      map[`${baseSnake}_value_web`] = M(
        `${baseCamel}ValueWeb`,
        parseMicroCurrency
      );
    }
  }

  // Custom events 1..5 — emit total + _app + _web variants per slot.
  for (const slot of CUSTOM_EVENT_SLOTS) {
    map[`custom_event_${slot}`] = M(`customEvent${slot}`);
    map[`custom_event_${slot}_app`] = M(`customEvent${slot}App`);
    map[`custom_event_${slot}_web`] = M(`customEvent${slot}Web`);
  }

  return map;
}

function capitalize(s: string): string {
  if (s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const FIELD_MAP: Record<string, FieldSpec> = buildFieldMap();

// ============================================================================
// Per-row transformer
// ============================================================================

/**
 * Coerce one raw Snap row into a typed record, then stamp `_reportType`.
 *
 * Only fields present in `FIELD_MAP` are emitted — unknown fields from a
 * future Snap schema addition are silently dropped (forward-compatible).
 *
 * Internal `_*` prefixed keys (`_finalizedDataEndTime`, `_entityId`, etc.)
 * pass through untouched — they're stamped by `parseSnapchatAdsSyncResponse`
 * upstream and consumed by the orchestrator for `metadata.dataFinalizedThrough`.
 */
function transformRow(
  raw: Record<string, unknown>,
  reportType: SnapchatAdsReportType
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [snapKey, value] of Object.entries(raw)) {
    if (snapKey.startsWith("_")) {
      // Pass through orchestrator-added meta fields untouched.
      out[snapKey] = value;
      continue;
    }
    const spec = FIELD_MAP[snapKey];
    if (!spec) continue;
    out[spec.out] = spec.parse(value);
  }
  out._reportType = reportType;
  return out;
}

// ============================================================================
// Public per-report-type transformer functions
// ============================================================================

export function transformOverviewRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "overview"));
}
export function transformCampaignsRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "campaigns"));
}
export function transformAdSquadsRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "adSquads"));
}
export function transformAdsRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "ads"));
}
export function transformAgeGenderRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "ageGender"));
}
export function transformCountryOsRows(
  rows: ReadonlyArray<Record<string, unknown>>
): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "countryOs"));
}

/** Dispatch table mirroring `pinterest/transformers.TRANSFORMERS`. */
export const TRANSFORMERS: Record<
  SnapchatAdsReportType,
  (rows: ReadonlyArray<Record<string, unknown>>) => Array<Record<string, unknown>>
> = {
  overview: transformOverviewRows,
  campaigns: transformCampaignsRows,
  adSquads: transformAdSquadsRows,
  ads: transformAdsRows,
  ageGender: transformAgeGenderRows,
  countryOs: transformCountryOsRows,
};

// ============================================================================
// Test-only export
// ============================================================================

/** Exposed for tests that audit the field-map vs catalogue coverage. */
export const __FIELD_MAP_KEYS_FOR_TESTS = Object.keys(FIELD_MAP);
